<template>
    <section class="dp-layout course-config-page">
        <section class="dp-left">
            <DepartmentPane directory-scope="school" @tree-node-click="treeNodeClick" :type="1"></DepartmentPane>
        </section>

        <section class="dp-right">
            <div class="config-panel">
                <div v-if="loading" class="config-loading">
                    <div class="config-loading__inner">
                        <i class="el-icon-loading"></i>
                        <p>正在加载配置...</p>
                    </div>
                </div>

                <div v-else-if="DepartmentId > 0" class="config-content">
                    <el-card class="config-card" shadow="never">
                        <div slot="header" class="card-header">
                            <div class="card-header__main">
                                <span class="title">特殊课程配置</span>
                                <span class="card-header__school">{{ departmentName || '未命名学校' }}</span>
                            </div>
                            <el-button type="primary" size="mini" icon="el-icon-check" :loading="saving"
                                @click="handleSave">保存配置</el-button>
                        </div>

                        <div class="setting-section">
                            <div class="setting-section__head">
                                <span class="setting-section__title">点名时间</span>
                            </div>
                            <div class="setting-grid">
                                <div class="setting-tile">
                                    <div class="tile-top">
                                        <div class="tile-label">
                                            <i class="el-icon-time"></i>每节课允许提前点名
                                        </div>
                                        <div class="tile-control">
                                            <el-input-number v-model="configForm.EarlyMarkAttendanceMinutes"
                                                :min="0" :max="120" controls-position="right"></el-input-number>
                                            <span class="tile-unit">分钟</span>
                                        </div>
                                    </div>
                                    <p class="tile-tip">0 表示必须到开课时间后才能点名；例如设为 10 表示开课前 10 分钟即可点名</p>
                                </div>
                            </div>
                        </div>

                        <div class="setting-section">
                            <div class="setting-section__head">
                                <span class="setting-section__title">补点与通知</span>
                                <span class="setting-section__desc">仅作用于特殊课程点到与补点相关规则</span>
                            </div>
                            <div class="setting-grid">
                                <div class="setting-tile">
                                    <div class="tile-top">
                                        <div class="tile-label">
                                            <i class="el-icon-edit-outline"></i>允许老师课后补点到
                                        </div>
                                        <div class="tile-control">
                                            <el-switch v-model="configForm.AllowMakeupAttendance" active-text="允许"
                                                inactive-text="禁止" @change="handleAllowMakeupChange"></el-switch>
                                        </div>
                                    </div>
                                </div>

                                <div v-if="configForm.AllowMakeupAttendance" class="setting-subgrid">
                                    <div class="setting-tile">
                                        <div class="tile-top">
                                            <div class="tile-label">
                                                <i class="el-icon-time"></i>课后补点到时限
                                            </div>
                                            <div class="tile-control">
                                                <el-input-number v-model="configForm.MakeupAttendanceTimeLimit"
                                                    :min="1" :max="30" controls-position="right"></el-input-number>
                                                <span class="tile-unit">天</span>
                                            </div>
                                        </div>
                                        <p class="tile-tip">1 为当天补点到，2 为第二天 23:59 前补点到，以此类推</p>
                                    </div>

                                    <div class="setting-tile">
                                        <div class="tile-top">
                                            <div class="tile-label">
                                                <i class="el-icon-bell"></i>任课老师下课未点到推送通知
                                            </div>
                                            <div class="tile-control">
                                                <el-switch v-model="configForm.PushNotificationOnMissingAttendance"
                                                    active-text="开启" inactive-text="关闭"></el-switch>
                                            </div>
                                        </div>
                                        <p class="tile-tip">特殊课程：任课老师当节课忘记点到，在下课时是否推送通知</p>
                                    </div>

                                    <div class="setting-tile setting-tile--readonly">
                                        <div class="tile-top">
                                            <div class="tile-label">
                                                <i class="el-icon-link"></i>推送任务 ID
                                            </div>
                                            <div class="tile-control">
                                                <span class="tile-readonly-value">{{
                                                    configForm.PushNotificationOnMissingAttendanceTaskId || '—' }}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="setting-section">
                            <div class="setting-section__head">
                                <span class="setting-section__title">点名权限</span>
                                <span class="setting-section__desc">控制任课教师在移动端的特殊课程点名范围</span>
                            </div>
                            <div class="setting-grid">
                                <div class="setting-tile">
                                    <div class="tile-top">
                                        <div class="tile-label">
                                            <i class="el-icon-s-custom"></i>允许切换班级点名
                                        </div>
                                        <div class="tile-control">
                                            <el-switch v-model="configForm.AllowTeacherSwitchClassForMarking"
                                                active-text="允许" inactive-text="不允许"></el-switch>
                                        </div>
                                    </div>
                                    <p class="tile-tip">关闭后，任课教师在移动端仅能对本班当节特殊课程点到</p>
                                </div>
                            </div>
                        </div>

                        <div class="setting-section">
                            <div class="setting-section__head">
                                <span class="setting-section__title">代调课申请</span>
                                <span class="setting-section__desc">特殊课程代调课原因显示名称（与普通课配置相互独立）</span>
                            </div>
                            <div class="setting-grid">
                                <div class="setting-tile setting-tile--reason-pair">
                                    <div class="tile-label">
                                        <i class="el-icon-edit-outline"></i>调代课申请原因名称
                                    </div>
                                    <div class="reason-name-row">
                                        <label
                                            v-for="reason in (configForm.CourseAdjustmentReasonTypes || [])"
                                            :key="reason.Value"
                                            class="reason-name-item">
                                            <span class="reason-name-no">{{ reason.Value }}</span>
                                            <el-input
                                                v-model="reason.Name"
                                                size="small"
                                                maxlength="5"
                                                placeholder="名称"
                                                class="reason-name-input" />
                                        </label>
                                    </div>
                                    <p class="tile-tip">编号固定 1~4，仅可改名称（最多 5 字）。统计/申请记录/H5 均按此显示。</p>
                                </div>
                            </div>
                        </div>
                    </el-card>
                </div>

                <div v-else class="no-department-tip">
                    <div class="no-department-tip__inner">
                        <i class="el-icon-school"></i>
                        <p>请在左侧选择一个学校，查看和配置特殊课程考勤</p>
                    </div>
                </div>
            </div>
        </section>
    </section>
</template>

<script>
import DepartmentPane from '../../components/DepartmentPane'
import { getSpecialCourseConfig, saveSpecialCourseConfig } from '../../api/modules/schedule-course';
export default {
    components: { DepartmentPane },
    name: 'SpecialCourseConfig',
    data() {
        return {
            configForm: {
                PushNotificationOnMissingAttendance: false,
                PushNotificationOnMissingAttendanceTaskId: '',
                AllowMakeupAttendance: false,
                MakeupAttendanceTimeLimit: 1,
                AllowTeacherSwitchClassForMarking: false,
                EarlyMarkAttendanceMinutes: 0,
                CourseAdjustmentReasonTypes: [
                    { Value: 1, Name: '因公' },
                    { Value: 2, Name: '因私' },
                    { Value: 3, Name: '病假' },
                    { Value: 4, Name: '其他' }
                ]
            },
            loading: false,
            saving: false,
            DepartmentId: 0,
            departmentName: '',
        };
    },
    methods: {
        treeNodeClick(data) {
            this.DepartmentId = data.value
            this.departmentName = data.label || '';
            this.getConfigData();
        },

        handleAllowMakeupChange(val) {
            if (!val) {
                this.configForm.PushNotificationOnMissingAttendance = false;
            }
        },

        getConfigData() {
            this.loading = true;
            getSpecialCourseConfig({ DepartmentId: this.DepartmentId }).then(res => {
                this.loading = false;
                if (res && res.data.response) {
                    const data = res.data.response;
                    this.configForm = {
                        DepartmentId: this.DepartmentId,
                        PushNotificationOnMissingAttendance: !!data.PushNotificationOnMissingAttendance,
                        PushNotificationOnMissingAttendanceTaskId: data.PushNotificationOnMissingAttendanceTaskId || '',
                        AllowMakeupAttendance: !!data.AllowMakeupAttendance,
                        MakeupAttendanceTimeLimit: data.MakeupAttendanceTimeLimit || 1,
                        AllowTeacherSwitchClassForMarking: !!data.AllowTeacherSwitchClassForMarking,
                        EarlyMarkAttendanceMinutes: data.EarlyMarkAttendanceMinutes || 0,
                        CourseAdjustmentReasonTypes: (data.CourseAdjustmentReasonTypes && data.CourseAdjustmentReasonTypes.length)
                            ? data.CourseAdjustmentReasonTypes.map(x => ({ Value: x.Value, Name: x.Name }))
                            : this.defaultReasonTypes()
                    };
                } else {
                    this.configForm = {
                        DepartmentId: this.DepartmentId,
                        PushNotificationOnMissingAttendance: false,
                        PushNotificationOnMissingAttendanceTaskId: '',
                        AllowMakeupAttendance: false,
                        MakeupAttendanceTimeLimit: 1,
                        AllowTeacherSwitchClassForMarking: false,
                        EarlyMarkAttendanceMinutes: 0,
                        CourseAdjustmentReasonTypes: this.defaultReasonTypes()
                    };
                }
            }).catch(() => {
                this.loading = false;
                this.$message.error('获取配置失败，请稍后重试');
            });
        },

        defaultReasonTypes() {
            return [
                { Value: 1, Name: '因公' },
                { Value: 2, Name: '因私' },
                { Value: 3, Name: '病假' },
                { Value: 4, Name: '其他' }
            ];
        },

        handleSave() {
            this.saving = true;
            const saveData = {
                ...this.configForm,
                DepartmentId: this.DepartmentId,
                CourseAdjustmentReasonTypes: (this.configForm.CourseAdjustmentReasonTypes || []).map(x => ({
                    Value: Number(x.Value),
                    Name: (x.Name || '').trim()
                }))
            };

            saveSpecialCourseConfig(saveData).then(res => {
                this.saving = false;
                if (res && res.data.success) {
                    this.$message.success('配置保存成功');
                    this.getConfigData();
                } else {
                    this.$message.error((res && res.data && res.data.msg) || res.message || '保存失败，请稍后重试');
                }
            }).catch(() => {
                this.saving = false;
                this.$message.error('保存配置失败，请稍后重试');
            });
        },
    }
};
</script>

<style src="../../style/course-config-page.css"></style>
